import { Dialog, IconButton } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbTransfer, TbBuildingBank } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import {
  createInternalTransfer,
  createValutWithdrawalRequest,
  fetchInternalTransferOptions,
  getAccountCurrencyCodes,
  getInternalTransferRoleLabelKey,
  getTransferErrorMessage,
  isEligibleInternalTransferSource,
  isEligibleShellFundSource,
  isShellFundDestination,
  isShellFundRootLocked,
} from "../../../../../Services/baasInternalTransfersApi";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const MODE_TRANSFER = "transfer";
const MODE_VALUT_WITHDRAWAL = "valut_withdrawal";

const formatAccountOption = (account) => {
  if (!account) return "";
  const roleKey = getInternalTransferRoleLabelKey(account.role);
  const roleLabel = roleKey ? intl.get(roleKey) : account.role;
  const virtualTag = account.isVirtual
    ? ` · ${intl.get("baas.accounts.transfer.virtualTag")}`
    : "";
  return `${roleLabel} — ${account.name}${virtualTag}`;
};

export const BaasAccountTransferModal = ({
  open,
  onClose,
  sourceAccountId = null,
  /** Restrict destinations to instance shells (OPERATING / OVERDRAFT / PROGRAM). */
  fundShells = false,
  destinationAccountId = null,
  destinationRole = null,
}) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();

  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [destinationAccounts, setDestinationAccounts] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [formError, setFormError] = useState(null);

  const [mode, setMode] = useState(MODE_TRANSFER);
  const [fromAccountId, setFromAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [sourceCurrency, setSourceCurrency] = useState("");
  const [destinationCurrency, setDestinationCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || !selectedInstance) {
      setSourceAccounts([]);
      setDestinationAccounts([]);
      setVirtualAccounts([]);
      setOptionsError(null);
      return undefined;
    }

    (async () => {
      setOptionsLoading(true);
      setOptionsError(null);
      try {
        const next = await fetchInternalTransferOptions(selectedInstance);
        if (cancelled) return;
        setSourceAccounts(next.sourceAccounts);
        setDestinationAccounts(next.destinationAccounts);
        setVirtualAccounts(next.virtualAccounts);
      } catch (error) {
        if (cancelled) return;
        setSourceAccounts([]);
        setDestinationAccounts([]);
        setVirtualAccounts([]);
        setOptionsError(getTransferErrorMessage(error, intl.get.bind(intl)));
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedInstance]);

  const instanceRootAccountId = String(selectedInstance?.rootAccountId || "").trim() || null;
  const shellFundOpts = { rootAccountId: instanceRootAccountId };
  const isEligibleFrom = fundShells
    ? (account) => isEligibleShellFundSource(account, shellFundOpts)
    : isEligibleInternalTransferSource;
  const transferSources = sourceAccounts.filter(isEligibleFrom);
  const fromCandidates =
    mode === MODE_TRANSFER
      ? transferSources
      : virtualAccounts.filter((account) => account.canRequestValutWithdrawal);
  const fromAccount = fromCandidates.find((account) => account.id === fromAccountId);
  const fromIsRoot = Boolean(fromAccount) && isShellFundRootLocked(fromAccount, shellFundOpts);
  // Abastecer shells: only OPERATING / OVERDRAFT / PROGRAM as destinations; source = Root only.
  // Contas: when debiting Root, also hide Root destinations (exit-rail is not a fund target).
  const toCandidates = fundShells
    ? destinationAccounts.filter((account) => isShellFundDestination(account, shellFundOpts))
    : fromIsRoot
      ? destinationAccounts.filter((account) => !isShellFundRootLocked(account, shellFundOpts))
      : destinationAccounts;
  const toAccount = toCandidates.find((account) => account.id === toAccountId);
  const preferredRole = String(destinationRole || (fundShells ? "program_reserve" : "")).toLowerCase();

  const sourceCurrencies = useMemo(
    () => getAccountCurrencyCodes(fromAccount),
    [fromAccount],
  );
  const destinationCurrencies = useMemo(
    () => getAccountCurrencyCodes(toAccount),
    [toAccount],
  );

  const currencyMismatch =
    mode === MODE_TRANSFER &&
    Boolean(sourceCurrency) &&
    Boolean(destinationCurrency) &&
    sourceCurrency !== destinationCurrency;

  useEffect(() => {
    if (!open) {
      setMode(MODE_TRANSFER);
      setFromAccountId("");
      setToAccountId("");
      setSourceCurrency("");
      setDestinationCurrency("");
      setAmount("");
      setLoading(false);
      setFormError(null);
      return;
    }

    // Prefer the opened account when it can debit; otherwise first eligible source.
    // Shells/reserves stay destinations only — never unlock outbound from their balance.
    // Abastecer shells: prefer instance Root (only eligible debit source).
    const preferredSource =
      (sourceAccountId &&
        sourceAccounts.find(
          (account) => account.id === sourceAccountId && isEligibleFrom(account),
        )) ||
      (fundShells &&
        instanceRootAccountId &&
        sourceAccounts.find(
          (account) =>
            account.id === instanceRootAccountId && isEligibleFrom(account),
        )) ||
      sourceAccounts.find(isEligibleFrom) ||
      null;

    const preferredDestination =
      (destinationAccountId &&
        toCandidates.find((account) => account.id === destinationAccountId)) ||
      (preferredRole &&
        toCandidates.find((account) => String(account.role || "").toLowerCase() === preferredRole)) ||
      (sourceAccountId && toCandidates.find((account) => account.id === sourceAccountId)) ||
      null;

    setMode(MODE_TRANSFER);
    setFromAccountId(preferredSource?.id || "");
    setToAccountId(preferredDestination?.id || "");
    setAmount("");
    setLoading(false);
    setFormError(null);
  }, [
    open,
    sourceAccountId,
    destinationAccountId,
    preferredRole,
    sourceAccounts,
    destinationAccounts,
    fundShells,
    instanceRootAccountId,
  ]);

  useEffect(() => {
    if (!open || mode !== MODE_TRANSFER) return;
    if (!fromAccountId) return;
    if (fromCandidates.some((account) => account.id === fromAccountId)) return;
    setFromAccountId("");
  }, [open, mode, fromAccountId, fromCandidates]);

  useEffect(() => {
    if (!sourceCurrencies.length) {
      setSourceCurrency("");
      return;
    }
    setSourceCurrency((current) =>
      sourceCurrencies.includes(current) ? current : sourceCurrencies[0],
    );
  }, [sourceCurrencies]);

  useEffect(() => {
    if (mode !== MODE_TRANSFER) {
      setDestinationCurrency("");
      return;
    }
    if (!destinationCurrencies.length) {
      setDestinationCurrency("");
      return;
    }
    setDestinationCurrency((current) => {
      if (destinationCurrencies.includes(current)) return current;
      if (sourceCurrency && destinationCurrencies.includes(sourceCurrency)) {
        return sourceCurrency;
      }
      return destinationCurrencies[0];
    });
  }, [mode, destinationCurrencies, sourceCurrency]);

  useEffect(() => {
    if (mode !== MODE_TRANSFER) return;
    if (!toAccountId) return;
    if (toCandidates.some((account) => account.id === toAccountId)) return;
    setToAccountId("");
  }, [mode, toAccountId, toCandidates]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || !selectedInstance) return;

    const parsedAmount = Number(String(amount).replace(",", "."));
    if (!fromAccountId || !sourceCurrency) return;
    if (mode === MODE_TRANSFER && !toAccountId) return;
    if (mode === MODE_TRANSFER && !destinationCurrency) return;
    if (mode === MODE_TRANSFER && fromAccountId === toAccountId) {
      setFormError(intl.get("baas.accounts.transfer.sameAccount"));
      return;
    }
    if (
      mode === MODE_TRANSFER &&
      fundShells &&
      !isShellFundRootLocked(fromAccount, shellFundOpts)
    ) {
      setFormError(
        intl.get("baas.accounts.transfer.errorShellFundRootSource") ||
          intl.get("baas.accounts.transfer.errorSourceNotRoot"),
      );
      return;
    }
    if (
      mode === MODE_TRANSFER &&
      (fundShells || fromIsRoot) &&
      isShellFundRootLocked(toAccount, shellFundOpts)
    ) {
      setFormError(
        intl.get("baas.accounts.transfer.errorShellFundRoot") ||
          intl.get("baas.accounts.transfer.errorDestinationNotAllowed"),
      );
      return;
    }
    if (mode === MODE_TRANSFER && fundShells && !isShellFundDestination(toAccount, shellFundOpts)) {
      setFormError(
        intl.get("baas.accounts.transfer.errorShellFundRoot") ||
          intl.get("baas.accounts.transfer.errorDestinationNotAllowed"),
      );
      return;
    }
    if (mode === MODE_TRANSFER && sourceCurrency !== destinationCurrency) {
      setFormError(intl.get("baas.accounts.transfer.currencyMismatch"));
      return;
    }
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setFormError(intl.get("baas.accounts.transfer.invalidAmount"));
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      if (mode === MODE_VALUT_WITHDRAWAL) {
        await createValutWithdrawalRequest(selectedInstance, {
          fromAccountId,
          currencyCode: sourceCurrency,
          amountMajor: parsedAmount,
        });
        addToast({
          type: "success",
          title: intl.get("baas.accounts.transfer.valutSuccess"),
        });
      } else {
        await createInternalTransfer(selectedInstance, {
          fromAccountId,
          toAccountId,
          sourceCurrencyCode: sourceCurrency,
          destinationCurrencyCode: destinationCurrency,
          amountMajor: parsedAmount,
        });
        addToast({
          type: "success",
          title: intl.get("baas.accounts.transfer.success"),
        });
      }
      onClose?.();
    } catch (error) {
      setFormError(getTransferErrorMessage(error, intl.get.bind(intl)));
    } finally {
      setLoading(false);
    }
  };

  const submitDisabled =
    loading ||
    optionsLoading ||
    !fromAccountId ||
    !sourceCurrency ||
    !amount ||
    currencyMismatch ||
    (mode === MODE_TRANSFER && (!toAccountId || !destinationCurrency));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e7a73f]/15">
            {mode === MODE_VALUT_WITHDRAWAL ? (
              <TbBuildingBank className="h-5 w-5 text-[#a66f1d]" aria-hidden />
            ) : (
              <TbTransfer className="h-5 w-5 text-[#a66f1d]" aria-hidden />
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
              {mode === MODE_VALUT_WITHDRAWAL
                ? intl.get("baas.accounts.transfer.valutTitle")
                : fundShells
                  ? intl.get("baas.accounts.transfer.fundShellsTitle")
                  : intl.get("baas.accounts.transfer.title")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {mode === MODE_VALUT_WITHDRAWAL
                ? intl.get("baas.accounts.transfer.valutSubtitle")
                : fundShells
                  ? intl.get("baas.accounts.transfer.fundShellsSubtitle")
                  : intl.get("baas.accounts.transfer.subtitle")}
            </p>
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          aria-label={intl.get("baas.accounts.transfer.cancel")}
        >
          <MdClose />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4">
        {!fundShells && virtualAccounts.some((account) => account.canRequestValutWithdrawal) && (
          <div
            className="extract-view-tabs"
            role="tablist"
            aria-label={`${intl.get("baas.accounts.transfer.modeTransfer")} / ${intl.get("baas.accounts.transfer.modeValut")}`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODE_TRANSFER}
              className={`extract-view-tab disabled:cursor-not-allowed disabled:opacity-50 ${
                mode === MODE_TRANSFER ? "extract-view-tab--active" : ""
              }`}
              onClick={() => {
                setMode(MODE_TRANSFER);
                setFromAccountId(
                  sourceAccounts.find(isEligibleInternalTransferSource)?.id || "",
                );
                setToAccountId("");
              }}
              disabled={loading}
            >
              {intl.get("baas.accounts.transfer.modeTransfer")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODE_VALUT_WITHDRAWAL}
              className={`extract-view-tab disabled:cursor-not-allowed disabled:opacity-50 ${
                mode === MODE_VALUT_WITHDRAWAL ? "extract-view-tab--active" : ""
              }`}
              onClick={() => {
                setMode(MODE_VALUT_WITHDRAWAL);
                setFromAccountId(
                  virtualAccounts.find((account) => account.canRequestValutWithdrawal)
                    ?.id || "",
                );
                setToAccountId("");
              }}
              disabled={loading}
            >
              {intl.get("baas.accounts.transfer.modeValut")}
            </button>
          </div>
        )}

        {optionsError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {optionsError}
          </p>
        )}

        {formError ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}

        {mode === MODE_TRANSFER && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get(
              fundShells
                ? "baas.accounts.transfer.fundShellsHint"
                : "baas.accounts.transfer.ruleHint",
            )}
          </p>
        )}

        {mode === MODE_VALUT_WITHDRAWAL && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.transfer.valutHint")}
          </p>
        )}

        <div>
          <label
            htmlFor="baas-transfer-from"
            className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
          >
            {intl.get("baas.accounts.transfer.from")}
          </label>
          <select
            id="baas-transfer-from"
            className={inputClass}
            value={fromAccountId}
            onChange={(event) => setFromAccountId(event.target.value)}
            required
            disabled={optionsLoading || !fromCandidates.length}
          >
            <option value="">{intl.get("baas.accounts.transfer.selectAccount")}</option>
            {fromCandidates.map((account) => (
              <option key={account.id} value={account.id}>
                {formatAccountOption(account)}
              </option>
            ))}
          </select>
          {mode === MODE_TRANSFER && !transferSources.length && !optionsLoading && (
            <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
              {intl.get(
                fundShells
                  ? "baas.accounts.transfer.fundShellsNoSource"
                  : "baas.accounts.transfer.noRootSource",
              )}
            </p>
          )}
        </div>

        {mode === MODE_TRANSFER && (
          <div>
            <label
              htmlFor="baas-transfer-to"
              className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
            >
              {intl.get("baas.accounts.transfer.to")}
            </label>
            <select
              id="baas-transfer-to"
              className={inputClass}
              value={toAccountId}
              onChange={(event) => setToAccountId(event.target.value)}
              required
              disabled={optionsLoading || !toCandidates.length}
            >
              <option value="">{intl.get("baas.accounts.transfer.selectAccount")}</option>
              {toCandidates
                .filter((account) => account.id !== fromAccountId)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {formatAccountOption(account)}
                  </option>
                ))}
            </select>
            {!toCandidates.length && !optionsLoading && (
              <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
                {intl.get(
                  fundShells
                    ? "baas.accounts.transfer.fundShellsEmpty"
                    : "baas.accounts.transfer.noVirtualDestinations",
                )}
              </p>
            )}
          </div>
        )}

        {mode === MODE_TRANSFER ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="baas-transfer-source-currency"
                className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
              >
                {intl.get("baas.accounts.transfer.currencySource")}
              </label>
              <select
                id="baas-transfer-source-currency"
                className={inputClass}
                value={sourceCurrency}
                onChange={(event) => setSourceCurrency(event.target.value)}
                required
                disabled={!sourceCurrencies.length}
              >
                <option value="">{intl.get("baas.accounts.transfer.selectCurrency")}</option>
                {sourceCurrencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="baas-transfer-destination-currency"
                className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
              >
                {intl.get("baas.accounts.transfer.currencyDestination")}
              </label>
              <select
                id="baas-transfer-destination-currency"
                className={inputClass}
                value={destinationCurrency}
                onChange={(event) => setDestinationCurrency(event.target.value)}
                required
                disabled={!destinationCurrencies.length}
              >
                <option value="">{intl.get("baas.accounts.transfer.selectCurrency")}</option>
                {destinationCurrencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <label
              htmlFor="baas-transfer-currency"
              className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
            >
              {intl.get("baas.accounts.transfer.currency")}
            </label>
            <select
              id="baas-transfer-currency"
              className={inputClass}
              value={sourceCurrency}
              onChange={(event) => setSourceCurrency(event.target.value)}
              required
              disabled={!sourceCurrencies.length}
            >
              <option value="">{intl.get("baas.accounts.transfer.selectCurrency")}</option>
              {sourceCurrencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
        )}

        {currencyMismatch && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            {intl.get("baas.accounts.transfer.currencyMismatch")}
          </p>
        )}

        <div>
          <label
            htmlFor="baas-transfer-amount"
            className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-100"
          >
            {intl.get("baas.accounts.transfer.amount")}
          </label>
          <input
            id="baas-transfer-amount"
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
            required
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button type="button" className="btn-outline w-auto" onClick={onClose} disabled={loading}>
            {intl.get("baas.accounts.transfer.cancel")}
          </button>
          <button type="submit" className="btn-primary w-auto" disabled={submitDisabled}>
            {loading
              ? mode === MODE_VALUT_WITHDRAWAL
                ? intl.get("baas.accounts.transfer.valutProcessing")
                : intl.get("baas.accounts.transfer.processing")
              : mode === MODE_VALUT_WITHDRAWAL
                ? intl.get("baas.accounts.transfer.valutConfirm")
                : fundShells
                  ? intl.get("baas.accounts.transfer.fundShellsConfirm")
                  : intl.get("baas.accounts.transfer.confirm")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
